import { useCallback, useMemo, useState } from 'react';
import { Box, Button, SxProps, Theme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { anchorRef, bindTrigger } from 'material-ui-popup-state/hooks';

import {
  IconButton,
  SearchForm,
  TableContainer,
  TableHeader,
  FilterListIcon,
} from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import {
  usePaginationParams,
  BEFORE_PARAM,
  AFTER_PARAM,
} from '@occc-app/hooks/usePaginationParams';
import { useSearchParams, SEARCH_PARAM } from '@occc-app/hooks/useSearchParams';
import {
  Retailer_Network_User_Role,
  RetailerNetworkLocationGroupFragment,
  useRemoveUserFromRetailerNetworkMutation,
  useAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation,
  useDisassociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation,
  User_Role,
} from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  CreateRetailerNetworkUser,
  UpdateRetailerNetworkLocationGroups,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';

import { RetailerNetworkLocationGroupUsersTable } from './retailer-network-location-group-users-table.component';

import { useRetailerNetworkUserCreateMutation } from '../../../../data-access/mutations';
import { useGetRetailerNetworkUsers } from '../../../../data-access/queries';
import { AssociateUsersWithLocationGroupsFormValues } from '../../../components/associate-users-with-location-groups-dialog/associate-users-with-location-groups-dialog.types';
import { CreateAndAssociateNewUserWithLocationGroupsDialog } from '../../../components/create-and-associate-new-user-with-location-groups-dialog/create-and-associate-new-user-with-location-groups-dialog.component';
import { CreateAndAssociateNewUserWithLocationGroupsFormValues } from '../../../components/create-and-associate-new-user-with-location-groups-dialog/create-and-associate-new-user-with-location-groups-dialog.types';
import { AssociateUsersWithLocationGroupsDialog } from '../../../components/associate-users-with-location-groups-dialog/associate-users-with-location-groups-dialog.component';
import { useUserRoleFilter } from '../../../components/user-role-filter/use-user-role-filter.hook';
import { UserRoleFilter } from '../../../components/user-role-filter/user-role-filter.component';
import { RemoveUserFromRetailerNetworkDialog } from '../../../components/remove-user-from-retailer-network-dialog.component';
import { RemoveUserFromLocationGroupDialog } from '../../../components/remove-user-from-location-group-dialog.component';
import { routeCreator } from '../../../retailer-connect.utils';

type RetailerNetworkLocationGroupUsersTableContainerProps = {
  retailerNetworkUuid: string;
  locationGroup: RetailerNetworkLocationGroupFragment;
  title?: string;
  searchParamName?: string;
  beforeParamName?: string;
  afterParamName?: string;
  tableHeaderSx?: SxProps<Theme>;
};

export const RetailerNetworkLocationGroupUsersTableContainer = ({
  retailerNetworkUuid,
  locationGroup,
  title = 'Admins and Users',
  searchParamName,
  beforeParamName,
  afterParamName,
  tableHeaderSx,
}: RetailerNetworkLocationGroupUsersTableContainerProps) => {
  const navigate = useNavigate();
  const showMobilePhone = useIsUserPermittedTo(Feature.RETAILER_NETWORK_USER_MOBILE_PHONE_VIEW);
  const [search, setSearch] = useSearchParams({
    searchParamName: searchParamName ?? SEARCH_PARAM,
    beforeParamName: beforeParamName ?? BEFORE_PARAM,
    afterParamName: afterParamName ?? AFTER_PARAM,
  });
  const [paginationInfo, onPageChange] = usePaginationParams({ beforeParamName, afterParamName });
  const { userRoleFilter, userFilterPopupState, userFilterPopoverProps, setUserRoleFilter } =
    useUserRoleFilter();

  const [removeUserFromLocationGroupDialogOpen, setRemoveUserFromLocationGroupDialogOpen] =
    useState<boolean>(false);
  const [removeUserFromRetailerNetworkDialogOpen, setRemoveUserFromRetailerNetworkDialogOpen] =
    useState<boolean>(false);
  const [
    isAssociateExistingUserToLocationGroupsDialogOpen,
    setAssociateExistingUserToLocationGroupsDialogOpen,
  ] = useState<boolean>(false);
  const [
    isCreateAndAssociateNewUserWithLocationGroupsDialogOpen,
    setCreateAndAssociateNewUserWithLocationGroupsDialogOpen,
  ] = useState<boolean>(false);
  const [userUuidToRemove, setUserUuidToRemove] = useState<string | null>(null);
  const [removeUserFromLocationGroup, { loading: isRemoveUserFromLocationGroupInProgress }] =
    useDisassociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation({
      onCompleted: () => snackbar.success('User has been successfully removed from location group'),
      refetchQueries: ['retailerNetworkUsers'],
    });
  const [removeUserFromRetailerNetwork, { loading: isRemoveUserFromRetailerNetworkInProgress }] =
    useRemoveUserFromRetailerNetworkMutation({
      onCompleted: () =>
        snackbar.success('User has been successfully removed from retailer network'),
      refetchQueries: ['retailerNetworkUsers'],
    });

  const onRemoveUserFromRetailerNetwork = useCallback(async () => {
    if (userUuidToRemove) {
      await removeUserFromRetailerNetwork({
        variables: {
          retailerNetworkUuid,
          userUuid: userUuidToRemove,
        },
      });

      setUserUuidToRemove(null);
      setRemoveUserFromRetailerNetworkDialogOpen(false);
    }
  }, [removeUserFromRetailerNetwork, retailerNetworkUuid, userUuidToRemove]);

  const [retailerNetworkUserCreateMutation] = useRetailerNetworkUserCreateMutation();
  const handleExistingUserToLocationGroupDialogOpen = useCallback(() => {
    setAssociateExistingUserToLocationGroupsDialogOpen(
      !isAssociateExistingUserToLocationGroupsDialogOpen,
    );
  }, [isAssociateExistingUserToLocationGroupsDialogOpen]);

  const handleAssociateNewUserWithLocationGroupsDialogOpen = useCallback(() => {
    setCreateAndAssociateNewUserWithLocationGroupsDialogOpen(
      !isCreateAndAssociateNewUserWithLocationGroupsDialogOpen,
    );
  }, [isCreateAndAssociateNewUserWithLocationGroupsDialogOpen]);

  const onRemoveUserFromLocationGroupDialogConfirm = useCallback(async () => {
    if (userUuidToRemove) {
      await removeUserFromLocationGroup({
        variables: {
          retailerNetworkUuid,
          locationGroupUuids: [locationGroup.uuid],
          retailerNetworkUserUuids: [userUuidToRemove],
        },
      });
      setUserUuidToRemove(null);
      setRemoveUserFromLocationGroupDialogOpen(false);
    }
  }, [locationGroup.uuid, removeUserFromLocationGroup, retailerNetworkUuid, userUuidToRemove]);

  const handleRemoveUserFromLocationGroup = (userUuid: string) => {
    setRemoveUserFromLocationGroupDialogOpen(true);
    setUserUuidToRemove(userUuid);
  };

  const handleRemoveUserFromRetailerNetwork = (openCommerceUserUuid: string) => {
    setRemoveUserFromRetailerNetworkDialogOpen(true);
    setUserUuidToRemove(openCommerceUserUuid);
  };

  const handleRedirectToUserDetailsPage = useCallback(
    (userUuid: string) => {
      navigate(routeCreator.userDetails(retailerNetworkUuid, userUuid));
    },
    [navigate, retailerNetworkUuid],
  );

  const { loading, error, data, paginationProps } = useGetRetailerNetworkUsers({
    filter: {
      retailerNetworkUuidEquals: retailerNetworkUuid,
      locationGroupUuidEquals: locationGroup.uuid,
      roleIn:
        userRoleFilter && userRoleFilter !== 'all'
          ? [userRoleFilter]
          : [Retailer_Network_User_Role.SiteAdmin, Retailer_Network_User_Role.SiteUser],
      nameLike: search,
    },
    paginationInfo,
    onPageChange,
  });

  const [
    associateLocationGroupsWithUsers,
    { loading: associateLocationsWithRetailerNetworkUsersInProgress },
  ] = useAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation({
    onCompleted: () =>
      snackbar.success('Users have been successfully associated with location groups'),
    refetchQueries: ['retailerNetworkUsers'],
  });

  const onAssociateLocationGroupWithRetailerNetworkUsersDialogConfirm = useCallback(
    async ({ locationGroups, users }: AssociateUsersWithLocationGroupsFormValues) => {
      const locationGroupUuids = locationGroups.map((locationGroups) => locationGroups.uuid);
      const retailerNetworkUserUuids = users.map((user) => user.uuid);

      await associateLocationGroupsWithUsers({
        variables: {
          retailerNetworkUuid,
          locationGroupUuids,
          retailerNetworkUserUuids,
        },
      });

      setAssociateExistingUserToLocationGroupsDialogOpen(false);
    },
    [associateLocationGroupsWithUsers, retailerNetworkUuid],
  );

  const handleCreateNewUserAndAssociateWithLocation = async (
    values: CreateAndAssociateNewUserWithLocationGroupsFormValues,
  ) => {
    const locationGroupUuids = values.locationGroups.map((locationGroup) => locationGroup.uuid);

    await retailerNetworkUserCreateMutation({
      variables: {
        input: {
          locationGroupUuids,
          openCommerceUser: {
            name: {
              first: values.firstName,
              last: values.lastName,
            },
            email: values.email.trim(),
            userRole: User_Role.RcUser,
            phoneNumber: values.phoneNumber ? values.phoneNumber.trim() : undefined,
          },
          retailerNetworkUuid,
          role: values.userRole,
        },
      },
      onCompleted: () => {
        snackbar.success(
          'User has been successfully created and associated with location group(s)',
        );
      },
      refetchQueries: ['retailerNetworkUsers'],
    });

    handleAssociateNewUserWithLocationGroupsDialogOpen();
  };

  const tableActions = useMemo(() => {
    return (
      <Box display='flex' justifyContent='end' alignItems='center' width='100%'>
        <SearchForm
          key='search'
          onSubmit={(values) => {
            setSearch(values.search);
          }}
          initialValues={{ search }}
        />
        <IconButton
          {...bindTrigger(userFilterPopupState)}
          ref={anchorRef(userFilterPopupState)}
          sx={{ ml: 1 }}
        >
          <FilterListIcon fontSize='inherit' />
        </IconButton>

        <Can I={UpdateRetailerNetworkLocationGroups({ uuid: retailerNetworkUuid })}>
          <Can I={CreateRetailerNetworkUser()}>
            <Button
              key='addNewUserButton'
              color='primary'
              sx={{ minWidth: 'fit-content', ml: 2 }}
              variant='contained'
              onClick={handleAssociateNewUserWithLocationGroupsDialogOpen}
              role='button'
            >
              Add New
            </Button>
          </Can>
          <Button
            key='addExistingUserButton'
            color='primary'
            sx={{ minWidth: 'fit-content', ml: 2 }}
            variant='contained'
            onClick={handleExistingUserToLocationGroupDialogOpen}
            role='button'
          >
            Add Existing
          </Button>
        </Can>
      </Box>
    );
  }, [
    handleAssociateNewUserWithLocationGroupsDialogOpen,
    handleExistingUserToLocationGroupDialogOpen,
    search,
    setSearch,
    userFilterPopupState,
    retailerNetworkUuid,
  ]);

  return (
    <>
      <TableContainer>
        <TableHeader title={title} actions={tableActions} sx={tableHeaderSx} />
        <RetailerNetworkLocationGroupUsersTable
          data={data}
          loading={loading}
          error={error}
          isSearching={!!search}
          isPhoneNumberVisible={showMobilePhone}
          paginationProps={paginationProps}
          handleRedirectToUserDetailsPage={handleRedirectToUserDetailsPage}
          handleRemoveUserFromLocationGroup={handleRemoveUserFromLocationGroup}
          handleRemoveUserFromRetailerNetwork={handleRemoveUserFromRetailerNetwork}
        />
      </TableContainer>

      <UserRoleFilter
        userRoleFilter={userRoleFilter}
        userFilterPopoverProps={userFilterPopoverProps}
        setUserRoleFilter={setUserRoleFilter}
      />

      {removeUserFromLocationGroupDialogOpen && (
        <RemoveUserFromLocationGroupDialog
          open={removeUserFromLocationGroupDialogOpen}
          setOpen={setRemoveUserFromLocationGroupDialogOpen}
          onConfirm={onRemoveUserFromLocationGroupDialogConfirm}
          isRemoveUserFromLocationGroupInProgress={isRemoveUserFromLocationGroupInProgress}
        />
      )}

      {removeUserFromRetailerNetworkDialogOpen && (
        <RemoveUserFromRetailerNetworkDialog
          open={removeUserFromRetailerNetworkDialogOpen}
          setOpen={setRemoveUserFromRetailerNetworkDialogOpen}
          onConfirm={onRemoveUserFromRetailerNetwork}
          isRemoveUserFromRetailerNetworkInProgress={isRemoveUserFromRetailerNetworkInProgress}
        />
      )}

      {isAssociateExistingUserToLocationGroupsDialogOpen && (
        <AssociateUsersWithLocationGroupsDialog
          retailerNetworkUuid={retailerNetworkUuid}
          locationGroup={locationGroup}
          open={isAssociateExistingUserToLocationGroupsDialogOpen}
          onClose={handleExistingUserToLocationGroupDialogOpen}
          onSubmit={onAssociateLocationGroupWithRetailerNetworkUsersDialogConfirm}
          loading={associateLocationsWithRetailerNetworkUsersInProgress}
        />
      )}

      {isCreateAndAssociateNewUserWithLocationGroupsDialogOpen && (
        <CreateAndAssociateNewUserWithLocationGroupsDialog
          retailerNetworkUuid={retailerNetworkUuid}
          locationGroup={locationGroup}
          open={isCreateAndAssociateNewUserWithLocationGroupsDialogOpen}
          onClose={handleAssociateNewUserWithLocationGroupsDialogOpen}
          onSubmit={handleCreateNewUserAndAssociateWithLocation}
        />
      )}
    </>
  );
};
